/* eslint-disable unicorn/filename-case -- TODO rename later */
import { autoUpdate, flip, offset, shift, size, useFloating } from "@floating-ui/react";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@istari-frontend/ui";
import { useEffect, useState } from "react";

import { Check } from "~/assets/svg";
import { cn } from "~/utils";

function FloatingCommandMenu({
  value = "",
  onSelect,
  options,
  heading = "Options",
  allowAddOption = false,
}: {
  value?: string | null;
  onSelect: (s: string) => void;
  options: string[];
  heading?: string;
  allowAddOption?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [customOptions, setCustomOptions] = useState<string[]>([]);
  const [search, setSearch] = useState<string | null>(value);
  const [isFocused, setIsFocused] = useState(false);

  const allOptions = [...options, ...customOptions];

  const handleSelect = (currentValue: string) => {
    onSelect(currentValue);
    setSearch(currentValue);
    setOpen(false);
  };

  const handleAddCustom = () => {
    if (search && !allOptions.some((opt) => opt.toLowerCase() === search.toLowerCase())) {
      setCustomOptions([...customOptions, search]);
      onSelect(search);
      setSearch(search);
      setOpen(false);
    }
  };

  const { refs, floatingStyles } = useFloating({
    open,
    onOpenChange: setOpen,
    middleware: [
      offset(-27),
      flip(),
      shift(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    if (value) {
      setSearch(value);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (refs.floating.current && !refs.floating.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      setIsFocused(true);
      document.addEventListener("mousedown", handleClickOutside);
    }
    // Return clean up function to remove event listener after component dismount
    return () => {
      setIsFocused(false);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, refs.floating, setOpen]);

  const inputTextColor: string = !value && !isFocused ? "text-red-400" : "text-black";

  return (
    <div>
      <div ref={refs.setReference} className="rounded-lg border">
        | {/* Reference div for floating command. Contains text, '|', to help with sizing. */}
      </div>
      <div ref={refs.setFloating} style={floatingStyles} className="z-[999] w-80 rounded-lg border bg-white">
        <Command className="bg-popover z-50 rounded-lg border-0" filter={value === search ? () => 1 : undefined}>
          <button
            onClick={() => {
              if (!open) setOpen(true);
            }}
            className="flex h-9 w-full items-center gap-2 rounded-lg text-gray-500 [&_[data-slot=command-input-wrapper]]:w-full [&_[data-slot=command-input-wrapper]]:border-b-0"
          >
            <CommandInput
              placeholder={"Search or type to add..."}
              value={search ?? ""}
              onInput={() => onSelect("")}
              onValueChange={(s) => setSearch(s)}
              className={cn(inputTextColor)}
            />
          </button>
          {open && (
            <CommandList className="top-full right-0 left-0 max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white">
              {allowAddOption && (
                <CommandEmpty>
                  <div className="py-6 text-center text-sm">
                    <p className="text-muted-foreground">No option found.</p>
                    <Button size="sm" onClick={handleAddCustom} disabled={!search}>
                      Add
                    </Button>
                  </div>
                </CommandEmpty>
              )}
              <CommandGroup heading={heading}>
                {allOptions.map((option) => (
                  <CommandItem key={option} value={option} onSelect={handleSelect}>
                    <Check className={cn("mr-2 h-4 w-4", value === option ? "opacity-100" : "opacity-0")} />
                    {option}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          )}
        </Command>
      </div>
    </div>
  );
}

export default FloatingCommandMenu;
